/** Barangay profile, puroks/sitios and logos (FR-ADM-04: onboarding by configuration). */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { fileObjectUrl, uploadFile } from '../../lib/files';
import { useAuth } from '../../lib/auth';
import { Alert, Button, Card, Field, PageHeader, Skeleton, inputClass } from '../../components/ui';

export interface BarangayView {
  id: string;
  portalEnabled?: boolean;
  name: string;
  cityMunicipality: string;
  province: string;
  region: string;
  psgcCode: string;
  hallAddress: string;
  contactNumber: string;
  email: string;
  logoFileId: string | null;
  cityLogoFileId: string | null;
  puroks: string[];
  status: 'SETUP' | 'ACTIVE' | 'SUSPENDED';
  setupSteps: string[];
  setupComplete: boolean;
  mfaRequired: boolean;
  isSandbox: boolean;
  shareAggregates: boolean;
  sectorTags: string[];
  privacyNoticeText: string;
  privacyNoticeVersion: string;
}

export const barangayQuery = { queryKey: ['barangay'], queryFn: () => api<BarangayView>('/barangay') };
export const useBarangay = () => useQuery(barangayQuery);

const TEXT: [keyof BarangayView, string, string?][] = [
  ['name', 'Barangay name', 'As it appears on documents, e.g. Barangay Lahug'],
  ['cityMunicipality', 'City / municipality'],
  ['province', 'Province'],
  ['region', 'Region', 'e.g. VII'],
  ['psgcCode', 'PSGC code', 'Optional'],
  ['hallAddress', 'Barangay hall address'],
  ['contactNumber', 'Contact number'],
  ['email', 'Email'],
];

function LogoCard({ title, field, value, editable }: { title: string; field: 'logoFileId' | 'cityLogoFileId'; value: string | null; editable: boolean }): JSX.Element {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let live = true;
    if (value) fileObjectUrl(`/files/${value}`).then((u) => { url = u; if (live) setPreview(u); }).catch(() => setPreview(null));
    else setPreview(null);
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [value]);
  const save = useMutation({
    mutationFn: async (file: File | null) => {
      const id = file ? (await uploadFile(file, field === 'logoFileId' ? 'barangay_logo' : 'city_logo')).id : null;
      return api<BarangayView>('/barangay', { method: 'PATCH', body: { [field]: id } });
    },
    onSuccess: (data) => qc.setQueryData(['barangay'], data),
  });
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">{title}</p>
      {save.error && <div className="mb-2"><Alert tone="critical">{save.error instanceof Error ? save.error.message : 'Upload failed.'}</Alert></div>}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-24 w-24 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-2 dark:border-slate-700">
          {preview ? <img src={preview} alt={title} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-400">None</span>}
        </div>
        {editable && (
          <div className="flex gap-2">
            <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) save.mutate(f); e.target.value = ''; }} />
            <Button type="button" variant="secondary" busy={save.isPending} onClick={() => input.current?.click()}>{value ? 'Replace' : 'Upload'}</Button>
            {value && <Button type="button" variant="ghost" onClick={() => { if (window.confirm(`Remove the ${title.toLowerCase()}?`)) save.mutate(null); }}>Remove</Button>}
          </div>
        )}
      </div>
    </div>
  );
}

export function BarangayPage(): JSX.Element {
  const { can } = useAuth();
  const editable = can('barangay.manage');
  const qc = useQueryClient();
  const query = useBarangay();
  const [form, setForm] = useState<Record<string, string>>({});
  const [puroks, setPuroks] = useState<string[]>([]);
  const [newPurok, setNewPurok] = useState('');
  const [tags, setTags] = useState('');
  const [notice, setNotice] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    setForm(Object.fromEntries(TEXT.map(([k]) => [k, String(query.data[k] ?? '')])));
    setPuroks(query.data.puroks);
    setTags(query.data.sectorTags.join(', '));
    setNotice(query.data.privacyNoticeText);
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => api<BarangayView>('/barangay', { method: 'PATCH', body: { ...form, puroks, sectorTags: tags.split(',').map((t) => t.trim()).filter(Boolean), privacyNoticeText: notice } }),
    onSuccess: (data) => { qc.setQueryData(['barangay'], data); setSaved(true); setTimeout(() => setSaved(false), 2500); },
  });
  const fieldError = (name: string) => (save.error instanceof ApiError ? save.error.fields[name] : undefined);

  if (query.isLoading || !query.data) return <Skeleton className="h-96 w-full" />;
  const addPurok = () => {
    const p = newPurok.trim().replace(/\s+/g, ' ');
    if (p && !puroks.some((x) => x.toLowerCase() === p.toLowerCase())) setPuroks([...puroks, p]);
    setNewPurok('');
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Barangay profile" description="Printed on every certificate, form and report. Puroks and sitios are used for addresses, hazard zones and reports." />
      {save.error && !(save.error instanceof ApiError && Object.keys(save.error.fields).length) && <div className="mb-4"><Alert tone="critical">{save.error.message}</Alert></div>}
      {saved && <div className="mb-4"><Alert tone="ok">Saved.</Alert></div>}
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-6">
        <Card title="Details">
          <div className="grid gap-4 sm:grid-cols-2">
            {TEXT.map(([key, label, hint]) => (
              <Field key={key} label={label} hint={hint} error={fieldError(key)} required={key === 'name'}>
                <input className={inputClass} value={form[key] ?? ''} disabled={!editable} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
              </Field>
            ))}
          </div>
        </Card>
        <Card title="Puroks and sitios" description="In the order they should appear in lists and reports.">
          <div className="flex flex-wrap gap-2">
            {puroks.length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
            {puroks.map((p, i) => (
              <span key={p} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-sm dark:bg-slate-800">
                {p}
                {editable && (
                  <button type="button" aria-label={`Remove ${p}`} className="grid h-6 w-6 place-items-center rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700" onClick={() => setPuroks(puroks.filter((_, j) => j !== i))}>
                    <X aria-hidden="true" className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
          {editable && (
            <div className="mt-3 flex gap-2">
              <input className={inputClass} placeholder="e.g. Purok 1 — Sampaguita" value={newPurok} maxLength={80} onChange={(e) => setNewPurok(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPurok(); } }} />
              <Button type="button" variant="secondary" onClick={addPurok}><Plus aria-hidden="true" className="h-4 w-4" /> Add</Button>
            </div>
          )}
        </Card>
        <Card title="Registry settings" description="Sector tags residents can carry (senior citizen, youth and minor are computed from the birth date). The privacy notice is shown when a resident is registered; changing it creates a new version that is stored with each consent.">
          <Field label="Sector tags" hint="Comma-separated, e.g. PWD, SOLO_PARENT, 4PS, IP, OFW, OSY, PREGNANT_LACTATING, REGISTERED_VOTER">
            <input className={inputClass} value={tags} disabled={!editable} onChange={(e) => setTags(e.target.value)} />
          </Field>
          <div className="mt-4">
            <Field label={`Privacy notice (now ${query.data.privacyNoticeVersion})`} hint="RA 10173: what is collected, why, who sees it, how long it is kept, and how to ask for a copy or a correction.">
              <textarea className={`${inputClass} min-h-32`} value={notice} disabled={!editable} onChange={(e) => setNotice(e.target.value)} />
            </Field>
          </div>
        </Card>
        <Card title="Logos" description="The barangay seal and the city or municipal logo, printed on documents. PNG or JPEG.">
          <div className="flex flex-wrap gap-8">
            <LogoCard title="Barangay seal" field="logoFileId" value={query.data.logoFileId} editable={editable} />
            <LogoCard title="City / municipal logo" field="cityLogoFileId" value={query.data.cityLogoFileId} editable={editable} />
          </div>
        </Card>
        <PortalSwitch on={Boolean(query.data.portalEnabled)} editable={editable} />
        <ShareSwitch on={Boolean(query.data.shareAggregates)} editable={editable} />
        {editable && <div className="flex justify-end"><Button type="submit" busy={save.isPending}>Save</Button></div>}
      </form>
    </div>
  );
}

/** FR-POR-01/04: the citizen portal and public transparency page, switched on per barangay. */
function PortalSwitch({ on, editable }: { on: boolean; editable: boolean }): JSX.Element {
  const qc = useQueryClient();
  const toggle = useMutation({ mutationFn: () => api<BarangayView>('/barangay', { method: 'PATCH', body: { portalEnabled: !on } }), onSuccess: (d) => qc.setQueryData(['barangay'], d) });
  return (
    <Card title="Citizen portal" description="Residents create an online account, get verified at the hall, then request certificates and send feedback online. The public transparency page is published at the same time.">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm">{on ? <>On. Residents sign up at <strong>{window.location.origin}/portal</strong>.</> : 'Off.'}</span>
        {editable && <Button type="button" variant={on ? 'secondary' : 'primary'} busy={toggle.isPending} onClick={() => toggle.mutate()}>{on ? 'Turn off' : 'Turn on'}</Button>}
      </div>
    </Card>
  );
}

/** FR-RPT-03: opt in to the city/municipal consolidated figures (counts only). */
function ShareSwitch({ on, editable }: { on: boolean; editable: boolean }): JSX.Element {
  const qc = useQueryClient();
  const toggle = useMutation({ mutationFn: () => api<BarangayView>('/barangay', { method: 'PATCH', body: { shareAggregates: !on } }), onSuccess: (d) => qc.setQueryData(['barangay'], d) });
  return (
    <Card title="Share figures with the city or municipality" description="Population, households, sectors, documents, Lupon cases, blotter counts and collections are shared as totals only: never names, and nothing from the VAW Desk or BADAC.">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm">{on ? 'Shared.' : 'Not shared.'}</span>
        {editable && <Button type="button" variant={on ? 'secondary' : 'primary'} busy={toggle.isPending} onClick={() => toggle.mutate()}>{on ? 'Stop sharing' : 'Share'}</Button>}
      </div>
    </Card>
  );
}
